// constants/menuRegistry.ts
/**
 * Canonical menu structure for the platform.
 *
 * Backend mirror: apps/backend/api/constants/menu_registry.py
 * Keep these in sync when adding new menus.
 */

export type SubmenuDef = {
  key: string;
  label: string;
  /**
   * Account feature flag this submenu additionally requires: built, not
   * released for everyone. The one source for the nav item (SectionSubNav)
   * and the docs validator (a flagged feature never gets a public summary —
   * plan 2026-10-13-docs-llm-versions-signed-in-and-public, step B0).
   */
  featureFlag?: string;
};

export type MenuSectionDef = {
  key: string;
  label: string;
  submenus: SubmenuDef[];
  /**
   * Which surface this section drives. "main" (default) = the top app nav +
   * the docs taxonomy. "settings" = the Settings menu only; these sections
   * participate in per-account menu visibility (Superadmin → Clients) but are
   * deliberately excluded from the docs system (see src/docs/*) so a section
   * like "settings" never becomes a valid `module:` for a docs article.
   */
  surface?: "main" | "settings";
  /**
   * Whether this section is a docs taxonomy section. Defaults to true for
   * main-surface sections. "dashboard" sets this to false: it's a gateable
   * main-surface landing page, but has no customer docs and must NOT become a
   * docs `module:` key or add an (empty) docs sidebar section. See src/docs/*.
   */
  docsEligible?: boolean;
};

export const MENU_REGISTRY: MenuSectionDef[] = [
  {
    // Dashboard is the default landing/home — a gateable main-surface section
    // with NO submenus. Excluded from the docs taxonomy (docsEligible: false).
    key: "dashboard",
    label: "Dashboard",
    submenus: [],
    docsEligible: false,
  },
  {
    key: "insights",
    label: "Insights",
    submenus: [
      // "home" is the Insights landing page (/insights). It is a real gateable
      // submenu — not an ungated section index — so it can carry docs/help
      // topics and be resolved as an account's landing leaf. Accounts with an
      // explicit menu_visibility config were backfilled by
      // alembic/versions/20260729_insights_home_menu_vis_backfill.py.
      { key: "home", label: "Home", featureFlag: "insights_home" },
      { key: "chat", label: "Chat" },
      { key: "deep-dives", label: "Deep Dives" },
      { key: "dashboards", label: "Dashboards" },
      // Reports (2026-08-07-reports-ga-completion W1). Routes live at
      // /reports/* (pre-existing URLs; delivery e-mails may reference them),
      // gated as an Insights submenu. Existing explicit menu_visibility
      // configs are backfilled by
      // alembic/versions/20260807_reports_menu_vis_backfill.py.
      { key: "reports", label: "Reports" },
      // Analyst (2026-08-07-periodic-analyst §G). Routes live at /analyst/*
      // — scheduled analyses notify with an action_url of
      // /analyst/{public_id}, so the URL shape is already load-bearing.
      // Gated as an Insights submenu; existing explicit menu_visibility
      // configs are backfilled by
      // alembic/versions/20260812_analyst_menu_vis_backfill.py. The nav item
      // additionally carries featureFlag: "periodic_analysis_enabled" (see
      // SectionSubNav) so it stays hidden while the feature is dark.
      // One entry for three views of one subject. The two cross-run views
      // (Metrics, Open) briefly had their own submenu keys here — plan
      // 2026-09-19 § 6e — and are TABS on the Analyst page since plan
      // 2026-09-20-metrics-and-open-are-tabs-on-the-analyst-page. Their docs
      // and help topics carry `submenu: analyst` again, which is the one key
      // that gates all three.
      //
      // `20260920_analyst_pages_menu_vis_backfill.py` appended "metrics" and
      // "open" to customised accounts' `menu_visibility.insights` and STAYS.
      // Those two keys are now inert data: the nav renders from SECTION_ITEMS
      // and this registry, never from menu_visibility, and isSubmenuVisible is
      // only ever asked about a key a nav item already declares. Dropping the
      // migration would force-push a branch already in a PR and put dev's
      // alembic head ahead of the file tree for every other session.
      { key: "analyst", label: "Analyst", featureFlag: "periodic_analysis_enabled" },
    ],
  },
  {
    key: "activation",
    label: "Activation",
    submenus: [
      { key: "segments", label: "Segments" },
      { key: "discovery", label: "Discovery" },
      { key: "profiles", label: "Profiles" },
      // "campaigns" was "storefront-personalization" until 2026-08-05; explicit
      // menu_visibility configs were renamed by
      // alembic/versions/20260805_campaigns_menu_key_rename.py.
      { key: "campaigns", label: "Campaigns" },
      // Post-purchase surveys (plan 2026-08-13-shopify-post-purchase-survey,
      // step 9). Explicit menu_visibility configs are backfilled by
      // alembic/versions/20260813_surveys_menu_vis_backfill.py — without it the
      // item would be invisible for exactly the accounts a superadmin had
      // customized ("config present + key absent = hidden", useMenuVisibility).
      { key: "surveys", label: "Surveys" },
      // Journeys (plan 2026-10-02-journeys-v1, ADR-0340). Explicit
      // menu_visibility configs are backfilled by
      // alembic/versions/20261007_journeys_menu_vis.py; the nav item also
      // carries featureFlag "journeys" (SectionSubNav), so it stays hidden
      // until the feature is switched on per account.
      { key: "journeys", label: "Journeys", featureFlag: "journeys" },
    ],
  },
  {
    key: "data-management",
    label: "Data Management",
    submenus: [
      { key: "sources", label: "Sources" },
      { key: "flows", label: "Flows" },
      { key: "authorizations", label: "Authorizations" },
      { key: "destinations", label: "Destinations" },
      { key: "semantic-layer", label: "Semantic Layer" },
      { key: "glossary", label: "Business Context" },
      { key: "agents", label: "Agents" },
      // P&L statement configs (plan 2026-10-05-pnl-statement-mode, step 7).
      // Explicit menu_visibility configs are backfilled by
      // alembic/versions/20261005_fin_statements_menu_vis.py; the nav item
      // also carries featureFlag "financial_statements_enabled" (SectionSubNav)
      // so it stays hidden until the feature is switched on per account.
      { key: "financial-statements", label: "Financial Statements", featureFlag: "financial_statements_enabled" },
      // Targets & budgets, moved here from Settings (plan
      // 2026-10-09-targets-page-redesign-and-grid-entry, D0). Explicit
      // menu_visibility configs are backfilled by
      // alembic/versions/20261010_targets_menu_vis.py.
      { key: "targets", label: "Targets" },
      // GitHub/dbt connections. The nav item predates its registry entry
      // (plan 2026-10-13-data-management-integrations-submenu-and-github-dbt-docs);
      // explicit menu_visibility configs are backfilled by
      // alembic/versions/20261013_dm_integr_menu_vis.py. Same key as
      // settings/integrations: the registry is keyed per section.
      { key: "integrations", label: "Integrations" },
      // Customer data modeling (plan 2026-10-21-customer-data-modeling-platform,
      // P2.3). Explicit menu_visibility configs are backfilled by
      // alembic/versions/20261027_dm_models_menu_vis.py; the nav item carries
      // featureFlag "data_modeling", so it stays hidden until the feature is
      // switched on per account.
      { key: "models", label: "Models", featureFlag: "data_modeling" },
    ],
  },
  {
    key: "automations",
    label: "Automations",
    submenus: [
      { key: "monitors", label: "Monitors" },
      { key: "alerts", label: "Alerts" },
      { key: "website-scan", label: "Website Scan", featureFlag: "website_scan_enabled" },
    ],
  },
  {
    // Settings-surface section: gateable per account via Superadmin → Clients,
    // consumed by SettingsSidebar / MenuGuard (NOT the top app nav). Only the
    // optional/integration items are listed; core settings (account, users,
    // workspaces, profile) are intentionally NOT gateable. Mirror of the
    // backend MENU_REGISTRY["settings"]. Submenu keys track the route path.
    key: "settings",
    label: "Settings",
    surface: "settings",
    submenus: [
      { key: "integrations", label: "Integrations" },
      { key: "mcp", label: "MCP Connections" },
      { key: "analyst-performance", label: "Analyst Performance" },
      { key: "marketing-channels", label: "Marketing Channels" },
      { key: "preferences", label: "Preferences" },
    ],
  },
];

/** The feature flag a submenu requires, if any (see `SubmenuDef.featureFlag`). */
export function submenuFeatureFlag(sectionKey: string, submenuKey: string): string | undefined {
  return MENU_REGISTRY.find((s) => s.key === sectionKey)?.submenus.find((m) => m.key === submenuKey)?.featureFlag;
}
